import { Box, Button } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { PlusIcon } from '~/assets/icons';
import { BaseHeaderTitle } from '~/components/common';
import { useTranslation } from 'react-i18next';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { catchError, handleApiError, notify } from '~/utils/helper';
import AddTaskDialog from '../../admin/jobs/components/EditTaskDialog';
import ModalAlertDelete from '~/components/common/modal-alert-delete/ModalAlertDelete';

import usePermission from '~/hooks/usePermission';
import { PERMISSIONS } from '~/utils/constants/permission';

import { IPayloadDeletePriceComponent } from '~/utils/interface/estimate';
import { handleGetTask, handleGetTaskListFranchise } from '~/thunks/task/taskThunk';
import FranchiseTaskList from '~/components/franchise-task/FranchiseTaskList/FranchiseTaskList';
import EditFranchiseTask from '~/components/franchise-task/EditFranchiseTask/EditFranchiseTask';
import taskApi from '~/apis/task';
import { taskActions } from '~/thunks/task/taskSlice';
import { handleGetSelectAccounts } from '~/thunks/account/accountThunk';
import { jobActions } from '~/thunks/job/jobSlice';
import { handleGetTags } from '~/thunks/tag/tagThunk';
import { handleGetJobTypes } from '~/thunks/job-type/jobTypeThunk';
import dayjs from 'dayjs';
import EditTaskDialog from '../customers/components/EditTaskDialog';
import EditJobDialog from '../../admin/jobs/components/EditTaskDialog';
import { IEditTask } from '~/utils/interface/task';

const FranchiseTask = () => {
  const permissions = usePermission();

  const { t } = useTranslation(['admin/task', 'admin/component']);
  const [openEditDialogCommonTask, setOpenEditDialogCommonTask] = useState<boolean>(false);
  const [openEditJobDialog, setOpenEditJobDialog] = useState<boolean>(false);
  const [editDialogModeCommonTask, setEditDialogModeCommonTask] = useState<string>('');
  const [openEditCustomerDialog, setOpenEditCustomerDialog] = useState<boolean>(false);
  const [editDialogModeProject, setEditDialogModeProject] = useState<string>('');
  const [editDialogModeCustomer, setEditDialogModeCustomer] = useState<string>('');

  let { taskEdit } = useAppSelector((state) => state.task);
  const [data, setData] = useState<IEditTask>();
  const dispatch = useAppDispatch();

  const {
    filterFranchiseTaskList,
    needRefreshData,
    selectedIds,
    openAlertModalDelete,
    menuSelectedId,
    openAlertModalEdit,
  } = useAppSelector((state) => state.task);

  const handleSetOpenDialogCommonTask = async (open: boolean, _needRefreshData?: boolean) => {
    setOpenEditDialogCommonTask(open);
  };
  const handleSetOpenJobDialog = async (open: boolean, _needRefreshData?: boolean) => {
    setOpenEditJobDialog(open);
  };

  const handleOpenDialogCommonTask = () => {
    setEditDialogModeCommonTask(DIALOG_MODE_CREATE);
    setOpenEditDialogCommonTask(true);
  };
  const handleOpenProjectTask = () => {
    setEditDialogModeProject(DIALOG_MODE_CREATE);
    dispatch(taskActions.changeOpenModalEdit(true));
  };

  const handleSetOpenProjectTask = async (open: boolean) => {
    dispatch(taskActions.changeOpenModalEdit(open));
  };

  const handleSetOpenCustomerTask = async (open: boolean, needRefreshData?: boolean) => {
    if (openEditCustomerDialog) setOpenEditCustomerDialog(open);

    if (needRefreshData) dispatch(taskActions.changeNeedRefreshData(true));
  };

  const handleOpenEdit = async (id: string) => {
    const response = await dispatch(handleGetTask(id)).unwrap();
    setData(response);
    if (response.jobId) {
      setEditDialogModeProject(DIALOG_MODE_EDIT);
      setOpenEditJobDialog(true);
    } else if (response.customerId) {
      setEditDialogModeCustomer(DIALOG_MODE_EDIT);
      setOpenEditCustomerDialog(true);
    } else {
      setEditDialogModeCommonTask(DIALOG_MODE_EDIT);
      setOpenEditDialogCommonTask(true);
    }

    try {
    } catch (e) {
      handleApiError(e);
    }
  };

  const closeAlertDeleteModal = useCallback(() => {
    dispatch(taskActions.changeMenuSelectId(undefined));
    dispatch(taskActions.changeOpenModalDelete(false));
  }, [dispatch]);

  const onDeleteSelectedFranchiseTasks = useCallback(async () => {
    let payloadDeleteFranchiseTasks: IPayloadDeletePriceComponent =
      menuSelectedId !== undefined ? { ids: [menuSelectedId] } : { ids: [...selectedIds] };
    try {
      await taskApi.deleteTasks(payloadDeleteFranchiseTasks);
      notify('success', t('deleteSuccessMessage'));
      dispatch(taskActions.changeNeedRefreshData(true));
      dispatch(taskActions.changeOpenModalDelete(false));
    } catch (e: unknown) {
      catchError(e, (message) => {
        notify('error', message);
      });
    }
  }, [dispatch, menuSelectedId, selectedIds, t]);

  useEffect(() => {
    dispatch(handleGetTaskListFranchise({ data: filterFranchiseTaskList }))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });
  }, [dispatch, filterFranchiseTaskList, needRefreshData]);

  useEffect(() => {
    dispatch(handleGetJobTypes());
    dispatch(handleGetTags());
    dispatch(handleGetSelectAccounts({}));
    dispatch(jobActions.setScheduleCurrentTime(dayjs().format('YYYY-MM-DD')));
  }, [dispatch]);

  return (
    <Box className='w-full h-full'>
      <Box className='mt-[20px] flex flex-col min-[1080px]:flex-row item-center'>
        <Box className='flex flex-col min-[1080px]:flex-row min-[1080px]:flex-1 min-[1080px]:justify-between'>
          <BaseHeaderTitle title={t('title')} />
        </Box>
        {permissions[PERMISSIONS.projectTask_create] && (
          <Box className='flex flex-row mt-[10px] min-[1080px]:flex-row min-[1080px]:ml-[10px] min-[1080px]:mt-0 justify-between'>
            <Button
              className='h-[36px] px-[14px] py-[9.5px] text-[14px]/[16.8px] font-medium rounded-[8px] ml-[10px]'
              variant='contained'
              disableElevation
              startIcon={<PlusIcon className='w-[10px] h-[10px]' />}
              onClick={handleOpenProjectTask}
            >
              {t('addNewProjectTask')}
            </Button>
            <AddTaskDialog
              open={openAlertModalEdit}
              setOpen={handleSetOpenProjectTask}
              mode={editDialogModeProject}
              data={taskEdit?.id}
            />
          </Box>
        )}
        {permissions[PERMISSIONS.companyTask_create] && (
          <>
            <Box className='flex flex-row mt-[10px] min-[1080px]:flex-row min-[1080px]:ml-[10px] min-[1080px]:mt-0 justify-between'>
              <Button
                className='h-[36px] px-[14px] py-[9.5px] text-[14px]/[16.8px] font-medium rounded-[8px] ml-[10px]'
                variant='contained'
                disableElevation
                startIcon={<PlusIcon className='w-[10px] h-[10px]' />}
                onClick={handleOpenDialogCommonTask}
              >
                {t('addNew')}
              </Button>
              <EditFranchiseTask
                open={openEditDialogCommonTask}
                setOpen={handleSetOpenDialogCommonTask}
                mode={editDialogModeCommonTask}
                data={data}
              />
            </Box>
          </>
        )}

        {/* Dialog Customer */}
        <EditTaskDialog
          open={openEditCustomerDialog}
          mode={editDialogModeCustomer}
          setOpen={handleSetOpenCustomerTask}
          data={taskEdit}
        />

        {/* Dialog Project */}
        <EditJobDialog
          open={openEditJobDialog}
          mode={editDialogModeProject}
          data={taskEdit?.jobId}
          setOpen={handleSetOpenJobDialog}
        />
      </Box>
      <Box className='mt-[40px]'>
        <FranchiseTaskList
          onOpenEdit={handleOpenEdit}
          data={taskEdit}
          onOpenEditCustomer={handleOpenEdit}
          onOpenEditJob={handleOpenEdit}
        />
      </Box>
      <ModalAlertDelete
        open={openAlertModalDelete}
        description={t('alertDescription')}
        onCancel={closeAlertDeleteModal}
        onConfirm={onDeleteSelectedFranchiseTasks}
      />
    </Box>
  );
};

export default FranchiseTask;
